/**
 * Who is signed in, as the shell and the account page show it.
 *
 * A session is either a personal account, with a name, an email and maybe a
 * photo, or a shared team password, which belongs to nobody. Both need
 * something in the avatar and a line of text under it, and the two places that
 * show them (the sidebar menu and the account page) must agree, so the choice
 * is made once here.
 */
import { KeyRoundIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useSession } from "@/hooks/use-session";
import { initials } from "@/features/auth/initials";
import { cn } from "@/lib/utils";

export function useSessionIdentity() {
  const { t } = useTranslation();
  const { user, role, displayName } = useSession();

  return {
    user,
    // While viewing as an editor with no account, their roster name rather
    // than "team password", which would be true of nobody on screen.
    name: displayName ?? t("nav.teamPassword"),
    /** The email for a person; the access level for a team password. */
    detail: user?.email ?? (role ? t(`users.roles.${role}`) : ""),
    roleLabel: role ? t(`users.roles.${role}`) : t("users.roles.none"),
  };
}

export function SessionAvatar({
  size = "default",
  className,
}: {
  size?: "default" | "sm" | "lg";
  className?: string;
}) {
  const { user } = useSession();

  return (
    <Avatar size={size} className={cn("rounded-lg after:rounded-lg", className)}>
      {user?.photoUrl && (
        <AvatarImage
          src={user.photoUrl}
          alt=""
          // Google's avatar host refuses hotlinks that carry a referrer.
          referrerPolicy="no-referrer"
          className="rounded-lg"
        />
      )}
      <AvatarFallback
        className={cn(
          "rounded-lg font-medium",
          // Near-black: white on the cyan gradient is 2.6:1 at its darkest; this is 7.6:1 to 12.9:1 in both themes.
          user ? "brand-gradient text-neutral-950" : "bg-muted text-muted-foreground",
        )}
      >
        {user ? initials(user.name) : <KeyRoundIcon className="size-4" />}
      </AvatarFallback>
    </Avatar>
  );
}
